import { useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
import { EDITOR_LIMITS } from "@/payroll/editor/constants";

/** The composer's own text and focus. Nothing else lives here. */
export const useComposer = (storageKey: string) => {
  const [text, setText] = useLocalStorage(`${storageKey}:draft`, "");
  const field = useRef<HTMLTextAreaElement>(null);
  const pane = useRef<HTMLElement>(null);
  // Focus without writing anything, for "answer in your own words": filling
  // with an empty string would throw away a draft the reader had started.
  const focus = useCallback(() => field.current?.focus(), []);
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.length !== 1 || event.key === " ") return;
      const active = document.activeElement;
      if (!active || !pane.current?.contains(active)) return;
      if (
        event.target instanceof Element &&
        event.target.closest(
          "input, textarea, select, button, a, [contenteditable='true'], [role='button'], [role='menuitem'], [role='tab']",
        )
      ) {
        return;
      }
      event.preventDefault();
      setText((current) =>
        current.length < EDITOR_LIMITS.messageLength
          ? `${current}${event.key}`
          : current,
      );
      field.current?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [setText]);
  return { text, setText, field, pane, focus };
};

export type Composer = ReturnType<typeof useComposer>;
